import { Define } from '../../../core/decorators';
import { TAG_INPUT_TIME } from '../../../core/utils/constants/tags';
import { HTMLBnumInputNumber } from '../input-number/bnum-input-number';
import { HTMLBnumInput } from '../input/bnum-input';
import { ATTRIBUTE_TYPE } from '../input/bnum-input.internal/constants';
import { BnumInputNumericCreateOptions } from '../input/bnum-input.internal/types';

const TYPE = 'time';

/**
 * Input de temps.
 *
 * @category Input
 *
 * @structure Sans rien
 * <bnum-input-time></bnum-input-time>
 *
 * @structure Avec une légende
 * <bnum-input-time>Label du champ</bnum-input-time>
 *
 * @structure Avec une légende et un indice
 * <bnum-input-time>
 * Label du champ
 * <span slot="hint">Indice d'utilisation</span>
 * </bnum-input-time>
 *
 * @structure Avec un bouton
 * <bnum-input-time button="true" button-icon="add">Label du champ
 *   <span slot="button">Ajouter</span>
 * </bnum-input-time>
 *
 * @structure En erreur
 * <bnum-input-time min="05:00" data-value="04:00">Label du champ
 * </bnum-input-time>
 *
 * @structure Avec un état de succès
 * <bnum-input-time state="success">Label du champ
 *   <span slot="success">Le champ est valide !</span>
 * </bnum-input-time>
 *
 * @structure Avec une icône
 * <bnum-input-time icon="search">Label du champ</bnum-input-time>
 *
 * @structure Avec un bouton avec icône seulement
 * <bnum-input-time placeholder="LA LA !" button-icon="add">Label du champ
 * </bnum-input-time>
 *
 * @structure Désactivé
 * <bnum-input-time disabled>
 *   Label du champ
 * </bnum-input-time>
 *
 * @structure Complet
 * <bnum-input-time
 *   data-value="5"
 *   placeholder="Texte indicatif"
 *   type="text"
 *   state="error"
 *   icon="search"
 *   button="primary"
 *   button-icon="send"
 *   step="10"
 * >
 *   Label du champ
 *   <span slot="hint">Indice d'utilisation</span>
 *   <span slot="success">Le champ est valide !</span>
 *   <span slot="error">Le champ est invalide !</span>
 *   <span slot="button">Envoyer</span>
 * </bnum-input-time>
 *
 * @attr {string} (optional) (default: 'number') type - Type de l'input (text, password, email, etc.) Ne pas modifier, toujours 'number' pour ce composant.
 *
 */
@Define({ tag: TAG_INPUT_TIME })
export class HTMLBnumInputTime extends HTMLBnumInput {
	constructor() {
		super();
	}

	protected _p_getStylesheets(): CSSStyleSheet[] {
		return [
			...super._p_getStylesheets(),
			HTMLBnumInputNumber.AdditionnalStylesheet,
		];
	}

	protected _p_preload(): void {
		this.setAttribute(ATTRIBUTE_TYPE, TYPE);
	}

	/**
	 *@inheritdoc
	 */
	protected _p_buildDOM(): void {
		super._p_buildDOM();
	}

	/**
	 *@inheritdoc
	 */
	static _p_observedAttributes(): string[] {
		return super._p_observedAttributes().filter(x => x !== ATTRIBUTE_TYPE);
	}

	/**
	 * Crée une instance du composant avec les options fournies.
	 * @param label Texte du label principal.
	 * @param options Options d'initialisation (attributs et slots).
	 * @returns {HTMLBnumInputTime} Instance du composant.
	 */
	static Create(
		label: string,
		options: BnumInputNumericCreateOptions = {},
	): HTMLBnumInputNumber {
		const finalOptions = {
			type: TYPE,
			...options,
		};
		return super.Create(label, finalOptions) as HTMLBnumInputNumber;
	}
}
